// passkey-enroll.ts — shared "optional post-login passkey enrollment" step.
//
// Used by BOTH new-device login paths that bring a user on WITHOUT a passkey:
//   1. JOIN CODE flow    (login.ts renderJoin -> completeApprovedLogin, persist)
//   2. RECOVERY KEY flow (login.ts renderJoin -> mode==='recovery' success)
//
// CRITICAL INVARIANTS (product-owner hard requirements):
//   - The user is ALREADY LOGGED IN before this runs. This step is BEST-EFFORT.
//   - It NEVER logs the user out and NEVER blocks: on ANY failure the user is
//     shown a clear message with a "Continue" acknowledgement and is then routed
//     to the SAME destination as on success.
//   - It runs the create ceremony ONLY when the caller opted in (checkbox) AND
//     the account has ZERO existing passkeys. If the account already has >=1
//     passkey, it shows a brief informational message and proceeds — it does NOT
//     mint a duplicate automatically.
//   - This is a WebAuthn CREATE (registration) only. It NEVER performs a passkey
//     GET/assertion — login already happened via the join/recovery credential.
import { toast, confirmDialog } from '../components/dom.js';
import { createPasskey } from '../crypto/webauthn.js';
import { api } from '../api/client.js';
import { decideEnrollAction, enrollErrText } from './passkey-enroll-decision.js';

// Re-export the pure helpers so existing import sites keep working.
export { decideEnrollAction, enrollErrText } from './passkey-enroll-decision.js';
export type { EnrollDecision } from './passkey-enroll-decision.js';

// A begin function returns the register-ceremony options plus the account's
// current passkey count (non-PII). Both /me/passkeys/begin and
// /auth/join/register/begin satisfy this shape.
export interface EnrollBeginResult {
  ceremony_id: string;
  options: unknown;
  existing_passkeys: number;
}

export interface EnrollPasskeyOptions {
  // Starts the register ceremony bound to the just-logged-in account.
  beginFn: () => Promise<EnrollBeginResult>;
  // Completes the register ceremony (server-side FINISH), persisting the credential.
  finishFn: (ceremonyId: string, credential: unknown) => Promise<unknown>;
  // Whether the user asked to trust this device (the shared 'permanent' checkbox).
  checkboxChecked: boolean;
  // Where to go afterwards — invoked in ALL outcomes (skip / success / failure).
  // The user is already logged in; this only navigates.
  destination: () => void | Promise<void>;
}

// enrollPasskeyBestEffort runs the optional post-login passkey-create step and
// ALWAYS routes to `destination` afterwards. It never throws and never logs the
// user out — the caller can treat it as fire-and-await.
export async function enrollPasskeyBestEffort(opts: EnrollPasskeyOptions): Promise<void> {
  const { beginFn, finishFn, checkboxChecked, destination } = opts;

  // Gate 1 (pure): user did not opt in -> nothing to do, just proceed.
  if (decideEnrollAction(checkboxChecked, 0) === 'skip') {
    await destination();
    return;
  }

  try {
    const ceremony = await beginFn();
    const action = decideEnrollAction(checkboxChecked, ceremony.existing_passkeys);

    // Gate 2: account already has a passkey -> do NOT auto-create a duplicate.
    // Inform the user and proceed (they're signed in).
    if (action === 'inform') {
      toast("This device/account already has a passkey — you're signed in", 'ok');
      await destination();
      return;
    }

    // action === 'create': zero passkeys + opted in -> run the CREATE ceremony
    // (never a GET/assertion).
    const credential = await createPasskey(ceremony.options);
    await finishFn(ceremony.ceremony_id, credential);
    toast('Passkey created on this device', 'ok');
    await destination();
  } catch (e) {
    // BEST-EFFORT failure: the user REMAINS logged in. Explain why the passkey
    // couldn't be created, wait for an explicit acknowledgement ("Continue"),
    // then route to the SAME destination exactly as if nothing happened.
    confirmDialog(
      `We couldn't create a passkey on this device (${enrollErrText(e)}). ` +
        "You're still signed in — you can add a passkey later from Settings. Click OK to continue.",
    );
    await destination();
  }
}

// Convenience begin binding for the RECOVERY flow: the recovered user is
// authenticated, so the self-register-begin endpoint applies.
export const recoveryEnrollBegin = (): Promise<EnrollBeginResult> => api.auth.addPasskeyBegin();
